import React, { DragEvent, FunctionComponent, useCallback, useRef, useState } from 'react';
import Button from 'ui/atoms/button';
import Translate from 'ui/atoms/translate';
import * as Styled from './styled';
import Icon from 'ui/atoms/icon';
import Spacer from 'ui/atoms/spacer';
import FileUploadDocumentContainer from './file-upload-document-container';
import FileUploadDocumentType from './file-upload-document-type';
import FormGroup from 'src/ui/molecules/form-group';
import MultipleFileUploadDocumentType from './multiple-file-upload-document-type';
import MultipleFileUploadDocumentContainer from './multiple-file-upload-document-container';

export interface FileToUpload {
  file: File | null;
  documentType?: string;
}

export interface FileWithDocType {
  file: Blob | null;
  documentType: string | null;
}

export type FileType = 'pdf' | 'image' | 'csv';

interface FileUploadProps {
  handleFileInput: (event: any) => void;
  uploadedFiles: FileToUpload[];
  onRemoveDocument: (fileName?: string) => void;
  documentTypes?: Array<any>;
  onSelectDocumentType?: (documentType: string, fileName?: string) => void;
  showRequiredError?: boolean;
  showValidatedForm?: boolean;
  fileType?: FileType;
  multipleFiles?: boolean;
  error?: string;
  groupFiles?: boolean;
  isDocumentTypeRequired?: boolean;
}

export const FileUpload: FunctionComponent<FileUploadProps> = ({
  uploadedFiles,
  handleFileInput,
  onRemoveDocument,
  documentTypes,
  onSelectDocumentType,
  showRequiredError,
  showValidatedForm,
  fileType = 'pdf',
  multipleFiles,
  error,
  groupFiles,
  isDocumentTypeRequired = true,
}) => {
  const fileInput = useRef<any>(null);

  const [isFileDragged, setIsFileDragged] = useState(false);

  const getAcceptFileTypes = () => {
    if (fileType === 'pdf') {
      return '.pdf';
    }
    if (fileType === 'csv') {
      return '.csv';
    }
    return 'image/*';
  };

  const handleFileDrop = (event: any) => {
    event.preventDefault();
    setIsFileDragged(false);
    handleFileInput && handleFileInput(event);
  };

  const getDragAndDropTranslation = useCallback(() => {
    if (fileType === 'pdf' && multipleFiles) {
      return 'uploadPdf.multipleDragDropButton';
    } else if (fileType === 'pdf') {
      return 'uploadPdf.dragDropButton';
    } else if (fileType === 'csv') {
      return 'uploadCsv.dragDropButton';
    } else {
      return 'uploadImage.dragDropButton';
    }
  }, [fileType, multipleFiles]);

  console.info(uploadedFiles);

  return (
    <>
      <Styled.Form method="post" action="" encType="multipart/form-data">
        <Styled.FileUploadContainer
          $dragOver={isFileDragged}
          $error={showRequiredError || !!error?.length}
          onDragLeave={() => setIsFileDragged(false)}
          onDragOver={(event: DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            setIsFileDragged(true);
          }}
          onDrop={handleFileDrop}
        >
          <input
            className="box__file"
            type="file"
            onChange={handleFileInput}
            onClick={(event: any) => {
              event.target.value = null;
            }}
            accept={getAcceptFileTypes()}
            id="file"
            hidden={true}
            ref={fileInput}
            multiple={multipleFiles}
          />
          <Styled.TextContent>
            <Icon size="large" name="upload" color="primary" />
            <Spacer y={4} />
            <Translate
              name={getDragAndDropTranslation()}
              as="div"
              args={[
                (text, key) => (
                  <Button
                    variant="link"
                    onClick={() => fileInput?.current && fileInput.current.click()}
                    key={key}
                    inline
                  >
                    {text}
                  </Button>
                ),
              ]}
            />
            <Spacer y={2} />
            <Styled.SideTextContent>
              <Translate
                name={multipleFiles ? 'uploadPdf.multipleFileSize' : 'uploadPdf.fileSize'}
                as="span"
                args={[
                  (text) => (
                    <label htmlFor="file">
                      {' '}
                      <b>{text}</b>
                    </label>
                  ),
                ]}
              />
            </Styled.SideTextContent>
          </Styled.TextContent>
        </Styled.FileUploadContainer>
        {!!error?.length && <FormGroup error={error} spacing="none" />}
      </Styled.Form>
      {!groupFiles &&
        !!uploadedFiles?.length &&
        uploadedFiles.map((uploadedFile) => {
          if (!uploadedFile?.file?.name) return null;

          return (
            <React.Fragment key={uploadedFile.file.name}>
              <Spacer y={2} />
              <FileUploadDocumentContainer uploadedFile={uploadedFile} onRemoveDocument={onRemoveDocument} />
              <FileUploadDocumentType
                uploadedFile={uploadedFile}
                documentTypes={documentTypes}
                showValidatedForm={showValidatedForm}
                onSelectDocumentType={onSelectDocumentType}
              />
            </React.Fragment>
          );
        })}
      {groupFiles && !!uploadedFiles?.length && (
        <>
          <Spacer y={2} />
          <MultipleFileUploadDocumentContainer
            uploadedFiles={uploadedFiles}
            onRemoveAllDocuments={() => onRemoveDocument()}
          />
          <MultipleFileUploadDocumentType
            uploadedFiles={uploadedFiles}
            documentTypes={documentTypes}
            showValidatedForm={showValidatedForm}
            onSelectDocumentType={onSelectDocumentType}
            isDocumentTypeRequired={isDocumentTypeRequired}
          />
        </>
      )}
    </>
  );
};

export default FileUpload;
